import React, { createContext, useContext, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Eye, X, Sparkles, ArrowRight } from "lucide-react";

/**
 * Global Preview Mode UI.
 * - A sticky top banner whenever the auth context reports user.preview === true.
 * - A "Sign up to continue" modal opened either programmatically (via
 *   `usePreviewGate()`) or by the api.js axios interceptor when the server
 *   returns 403 + code:"preview_blocked".
 */

const PreviewCtx = createContext({
  openSignupGate: () => {},
  closeSignupGate: () => {},
});

export const usePreviewGate = () => useContext(PreviewCtx);

export function PreviewProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");

  const openSignupGate = useCallback((msg) => {
    setReason(msg || "Preview mode lets you explore TREL safely. Create a free account to make this real.");
    setOpen(true);
  }, []);
  const closeSignupGate = useCallback(() => setOpen(false), []);

  // Expose the opener globally so the api.js interceptor can call it without
  // needing React context.
  if (typeof window !== "undefined") {
    window.__trelOpenSignupGate = openSignupGate;
  }

  return (
    <PreviewCtx.Provider value={{ openSignupGate, closeSignupGate }}>
      {children}
      {open && (
        <div
          data-testid="preview-signup-modal"
          className="fixed inset-0 z-[300] bg-[#071A2D]/75 backdrop-blur-sm flex items-center justify-center px-6"
        >
          <div className="w-full max-w-md rounded-3xl bg-[#FFF8EE] border border-[#C9A45C]/40 shadow-luxury p-8 relative">
            <button
              data-testid="preview-signup-close"
              onClick={closeSignupGate}
              className="absolute top-4 right-4 text-[#071A2D]/55 hover:text-[#071A2D]"
              aria-label="Close"
            >
              <X size={18}/>
            </button>
            <div className="w-10 h-10 rounded-full bg-[#C9A45C]/20 flex items-center justify-center">
              <Sparkles size={18} className="text-[#C9A45C]"/>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9A45C] font-semibold mt-4">
              Preview Mode
            </div>
            <h2 className="font-serif text-2xl text-[#071A2D] mt-1">Create your free account to continue</h2>
            <p className="text-sm text-[#071A2D]/70 mt-3 leading-relaxed">{reason}</p>
            <div className="mt-6 space-y-2">
              <Link
                to="/signup"
                onClick={closeSignupGate}
                data-testid="preview-signup-cta"
                className="btn-coral w-full flex items-center justify-center gap-1.5"
              >
                Sign up free <ArrowRight size={14}/>
              </Link>
              <Link
                to="/login"
                onClick={closeSignupGate}
                data-testid="preview-login-cta"
                className="block text-center text-sm text-[#064E3B] underline mt-2 py-2"
              >
                Already have an account? Sign in
              </Link>
            </div>
            <div className="text-[11px] text-[#071A2D]/50 mt-4 text-center">
              Continue exploring TREL while you decide.
            </div>
          </div>
        </div>
      )}
    </PreviewCtx.Provider>
  );
}

/**
 * Sticky preview banner — only shown when `isPreview` is true.
 * Pure presentational; receives state from the auth context in the consumer.
 */
export function PreviewBanner({ isPreview }) {
  if (!isPreview) return null;
  return (
    <div
      data-testid="preview-banner"
      className="sticky top-0 z-[140] bg-[#C9A45C] text-[#071A2D] text-xs px-4 py-2 flex items-center gap-2 justify-center flex-wrap"
    >
      <Eye size={13}/>
      <span className="font-semibold">Preview Mode</span>
      <span className="opacity-80 hidden sm:inline">— exploring with sample data.</span>
      <Link
        to="/signup"
        data-testid="preview-banner-signup"
        className="ml-2 text-[11px] underline font-semibold hover:no-underline"
      >
        Create your account →
      </Link>
    </div>
  );
}
